<div class="d-flex flex-column h-100">
  @if (isAdmin()) {
    <si-form-item
      class="mb-4 ms-6"
      [label]="'WORKSPACE.SHOW_PRIVATE_WORKSPACES_OF_OTHER_USERS' | translate"
    >
      <input type="checkbox" class="form-check-input" [(ngModel)]="showOtherPrivateWorkspaces" />
    </si-form-item>
  }
  <div class="list-group-md flex-grow-1 overflow-auto">
    @for (workspace of prioritizedWorkspaces(); track workspace.id) {
      <li class="list-group-item list-group-item-action">
        <div class="d-flex w-100 justify-content-between align-items-center">
          <div>
            <div class="d-flex w-100 align-items-center">
              @if (isPinnedWorkspace(workspace)) {
                <i class="icon element-pin me-2 text-secondary" aria-hidden="true"></i>
              }
              <p class="si-title-1 m-0">
                {{ workspace.name }}
              </p>
              @if (isDefaultWorkspace(workspace)) {
                <span class="badge bg-info">{{ 'Default' | translate }}</span>
              }
            </div>
            <small class="text-secondary">
              @if (isOwnPrivateWorkspace(workspace)) {
                {{ 'WORKSPACE.PRIVATE_WORKSPACE_DESCRIPTION' | translate }}
              } @else if (workspace.id === 1) {
                {{ 'WORKSPACE.PUBLIC_WORKSPACE_DESCRIPTION' | translate }}
              } @else {
                {{ workspace.description }}
              }
            </small>
          </div>
          <button
            type="button"
            class="btn btn-circle btn-ghost element-options"
            aria-label="workspace options"
            [cdkMenuTriggerFor]="workspaceContextMenu"
            [cdkMenuTriggerData]="{ ws: workspace }"
          ></button>
        </div>
      </li>
    }
  </div>
  <div class="w-100 px-6 py-8 d-flex">
    <button
      type="button"
      class="btn btn-secondary btn-sm w-100"
      (click)="openModal(createWorkspaceModalTemplate)"
    >
      <i class="icon element-plus"></i>{{ 'SHARED.CREATE' | translate }}
    </button>
  </div>
</div>

<ng-template #workspaceContextMenu let-ws="ws">
  <si-menu>
    @if (canToggleDefaultWorkspace(ws)) {
      <si-menu-item icon="element-checked" (click)="toggleDefaultWorkspace(ws)">{{
        (isDefaultWorkspace(ws) ? 'WORKSPACE.UNSET_AS_DEFAULT' : 'WORKSPACE.SET_AS_DEFAULT')
          | translate
      }}</si-menu-item>
    }
    <si-menu-item
      icon="element-add-user"
      [disabled]="!canManageWorkspace(ws)"
      (click)="openManageUsersModal(manageUsersModalTemplate, ws)"
      >{{ 'WORKSPACE.MANAGE_USERS' | translate }}</si-menu-item
    >
    <si-menu-item
      icon="element-logout"
      [disabled]="!canLeaveWorkspace(ws)"
      (click)="showLeaveWorkspaceDialog(ws)"
      >{{ 'WORKSPACE.LEAVE' | translate }}</si-menu-item
    >
    <si-menu-item
      icon="element-delete"
      [disabled]="!canManageWorkspace(ws)"
      (click)="showDeleteWorkspaceDialog(ws)"
      >{{ 'SHARED.DELETE' | translate }}</si-menu-item
    >
  </si-menu>
</ng-template>

<!-- Add User Modal Template -->
<ng-template #createWorkspaceModalTemplate let-modalRef="modalRef">
  <div class="modal-header">
    <h4 class="modal-title" id="sample-modal-title">{{
      'WORKSPACE.CREATE_WORKSPACE' | translate
    }}</h4>
    <button
      type="button"
      class="btn btn-circle btn-ghost element-cancel"
      aria-label="Close modal"
      (click)="closeModal(modalRef)"
    ></button>
  </div>
  <div class="modal-body">
    <form class="d-flex flex-column h-100" [formGroup]="createNewWorkspaceForm">
      <si-form-container
        [form]="createNewWorkspaceForm"
        [errorCodeTranslateKeyMap]="errorCodeTranslateKeyMap"
      >
        <div si-form-container-content>
          <!-- Workspace Name -->
          <si-form-item label="Workspace Name">
            <input
              type="text"
              class="form-control"
              formControlName="workspaceName"
              placeholder="New Workspace"
            />
          </si-form-item>

          <!-- Description -->
          <si-form-item [label]="'SHARED.DESCRIPTION' | translate">
            <textarea
              class="form-control"
              formControlName="description"
              rows="3"
              [placeholder]="'WORKSPACE.NEW_WORKSPACE_DESCRIPTION_PLACEHOLDER' | translate"
            ></textarea>
          </si-form-item>

          <!-- Users -->
          <si-form-item label="Users" class="mb-8">
            <app-user-select
              [optionSource]="createWorkspaceUserSelectSource"
              [(selectedItems)]="selectedCreateWorkspaceUsers"
            />
          </si-form-item>

          <!-- Submit Button -->
          <button
            type="button"
            class="btn btn-secondary btn-sm w-100"
            [disabled]="createNewWorkspaceForm.invalid || selectedCreateWorkspaceUsers.length === 0"
            (click)="onCreateNewWorkspace(modalRef)"
          >
            <i class="icon element-add-user"></i>{{ 'WORKSPACE.CREATE_NEW_WORKSPACE' | translate }}
          </button>
        </div>
      </si-form-container>
    </form>
  </div>
</ng-template>

<!-- Manage Users Modal -->
<ng-template #manageUsersModalTemplate let-modalRef="modalRef" let-ws="ws">
  <div class="modal-header">
    <h4 class="modal-title">
      {{ 'WORKSPACE.MANAGE_USERS_TITLE' | translate: { name: ws?.name } }}
    </h4>
    <button
      type="button"
      class="btn btn-circle btn-ghost element-cancel"
      aria-label="Close modal"
      (click)="closeModal(modalRef)"
    ></button>
  </div>
  <div class="modal-body manage-users-modal-body d-flex flex-column gap-8">
    <!-- Members list -->
    <div class="members-section">
      <h6 class="si-body-2 fw-bold mb-4">{{ 'WORKSPACE.MEMBERS' | translate }}</h6>
      <div class="members-list">
        @for (member of workspaceMembers(); track member.oid) {
          <div class="d-flex align-items-center justify-content-between py-2 border-bottom">
            <div class="d-flex align-items-center gap-2">
              <i class="icon element-user"></i>
              @if (member.displayName) {
                <span>{{ member.displayName }}</span>
              } @else {
                <span>{{ member.gid }}</span>
                <button
                  si-help-button
                  type="button"
                  [siHelpTitle]="'WORKSPACE.UNKNOWN_USER' | translate"
                  [siHelpContent]="'WORKSPACE.UNKNOWN_USER_HELP' | translate"
                  ><!-- This text is only visible to screen reader. Provide a meaningful text, not only "help" -->
                  Why there is no name shown</button
                >
              }

              @if (member.gid === accountSummary().gid) {
                <span class="badge bg-default m-0">{{ 'WORKSPACE.YOU_BADGE' | translate }}</span>
              }
              @if (member.isCreator) {
                <span class="badge bg-info m-0">{{ 'WORKSPACE.CREATOR_BADGE' | translate }}</span>
              }
            </div>
            @if (canManageWorkspace(ws) && member.gid !== accountSummary().gid) {
              <div class="d-flex gap-1">
                <button
                  type="button"
                  class="btn btn-circle btn-ghost element-delete"
                  [attr.aria-label]="'WORKSPACE.REMOVE_USER' | translate"
                  (click)="showRemoveUserFromWorkspaceDialog(member, ws)"
                ></button>
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Add users section (all workspace members) -->
    @if (canManageWorkspace(ws)) {
      <div>
        <h6 class="si-body-2 fw-bold mb-4">{{ 'WORKSPACE.ADD_USERS_HEADING' | translate }}</h6>
        <app-user-select
          [optionSource]="manageUsersSelectSource"
          [(selectedItems)]="selectedManageUsers"
        />
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          [disabled]="selectedManageUsers.length === 0"
          (click)="onAddUsersToManagedWorkspace(ws)"
        >
          <i class="icon element-add-user"></i>
          {{ 'WORKSPACE.ADD_SELECTED_USERS' | translate }}
        </button>
      </div>
    }
  </div>
</ng-template>
